import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useBlocker, useNavigate } from "react-router";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  PageHeader,
  Select,
  Textarea,
  buttonStyles,
} from "../components/ui";
import { Modal } from "../components/Modal";
import { ApiError } from "../lib/api";
import { useCreateServer, useRegions } from "../lib/queries";
import type { ServerState } from "../lib/types";

/**
 * Register an engine, on a route of its own.
 *
 * This was a dialog holding nine fields, one of which is a credential and two of
 * which are coordinates that have to agree with each other. Registration is also
 * the one action here that talks to a machine before it succeeds — the key is
 * verified against the instance, which can take a moment and can fail with
 * something worth reading. A modal is a poor place to receive that: the error
 * arrives in a box the operator is trying to dismiss.
 *
 * On a route the half-filled form survives a reload, the failure has room to
 * explain itself, and the back button does what it looks like it does.
 */
export function ServerCreatePage() {
  const navigate = useNavigate();
  const create = useCreateServer();
  const { data: regions } = useRegions();

  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [originHost, setOriginHost] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [region, setRegion] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [maxOrgs, setMaxOrgs] = useState("");
  const [state, setState] = useState<ServerState>("active");
  const [notes, setNotes] = useState("");
  const [coordinateError, setCoordinateError] = useState<string | null>(null);

  /*
   * The selected region, but only when it is one the API flagged as wider than
   * the placement band. Read from the catalogue rather than listed here, so the
   * console never holds its own opinion about which regions are imprecise — that
   * is derived from `reachKm` on the API side and travels in the response.
   */
  const approximateRegion = (regions ?? [])
    .flatMap((entry) => entry.regions)
    .find((entry) => entry.code === region && entry.approximate);

  /*
   * Anything typed counts, and the credential is why. A dialog could only be
   * dismissed by its own controls; a route can be left by the nav rail, the back
   * button or a stray link — and what gets thrown away is an API key somebody
   * fetched out of another system's settings page and pasted here once.
   *
   * `beforeunload` covers the tab closing, the blocker covers navigation inside
   * the app, and both read the same answer to "is there anything to lose".
   */
  const dirty =
    !create.isSuccess &&
    Boolean(name || url || apiKey || region || latitude || longitude || maxOrgs || notes);

  /*
   * A ref beside `create.isSuccess`, for the same reason the model editor needs
   * one: `isSuccess` is a render-time value and `navigate` is called from inside
   * `onSuccess`, before the component has re-rendered with it. The blocker
   * therefore saw a dirty form and challenged the redirect of a server that had
   * just been created — asking whether to discard an API key that was already
   * accepted and encrypted.
   */
  const savedRef = useRef(false);
  const blocker = useBlocker(() => dirty && !savedRef.current);

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setCoordinateError(null);

    const lat = latitude.trim();
    const lon = longitude.trim();
    /* One without the other is not a location, and the placement query would
       silently treat the row as unlocated rather than as half-entered. */
    if (!!lat !== !!lon) {
      setCoordinateError("Latitude and longitude must be entered together.");
      return;
    }

    create.mutate(
      {
        name: name.trim(),
        url: url.trim(),
        // Omitted rather than sent empty: absent means "resolve the endpoint",
        // and an empty string would be stored as a direct address of nothing.
        ...(originHost.trim() ? { originHost: originHost.trim() } : {}),
        apiKey: apiKey.trim(),
        ...(region ? { region } : {}),
        ...(lat && lon ? { latitude: Number(lat), longitude: Number(lon) } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        maxOrgs: maxOrgs ? Number(maxOrgs) : null,
        state,
      },
      {
        onSuccess: (server) => {
          savedRef.current = true;
          navigate(`/servers/${server.id}`);
        },
      },
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Register an engine"
        description="The key is verified against the instance before anything is stored."
        action={
          <Link to="/servers" className={buttonStyles("secondary", "md")}>
            Back to servers
          </Link>
        }
      />

      <form onSubmit={onSubmit} className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0 space-y-4">
          {create.error && (
            <Alert>
              {create.error.message}
              {/*
                The provider's own words underneath ours. A registration fails for
                reasons only the engine knows — a rejected key, a TLS name that
                does not match — and paraphrasing those into "could not connect"
                throws away the sentence that tells an operator what to change.
              */}
              {create.error instanceof ApiError &&
                typeof create.error.details === "object" &&
                create.error.details !== null &&
                "message" in create.error.details && (
                  <p className="mt-1 text-xs opacity-80">
                    {String((create.error.details as { message: unknown }).message)}
                  </p>
                )}
            </Alert>
          )}

          <Card>
            <CardHeader title="Connection" description="Where we reach it, and as whom." />
            <div className="space-y-4 p-5">
              <Field label="Name" htmlFor="name" hint="How this server is referred to internally.">
                {/* The placeholder is not a region code — the region is picked
                    separately below, and a name that looks like one invites the
                    two being kept in sync by hand. It was `fra-1`, which read as
                    exactly that, and since migration 0045 `fra` is not a region at
                    all — so it suggested a code the picker below would reject. */}
                <Input
                  id="name"
                  required
                  placeholder="eu-web-01"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Field>

              <Field label="Endpoint" htmlFor="url">
                <Input
                  id="url"
                  type="url"
                  required
                  placeholder="https://engine.example.com"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                />
              </Field>

              {/*
                Optional, and the hint has to explain when it is not.
                
                A proxied endpoint hides the machine's address, so the platform
                cannot discover it — and writing what it resolves to instead put
                Cloudflare's own IP into a customer's CNAME target, which
                verified green and served Error 1000 on every request.
              */}
              <Field
                label="Direct address"
                htmlFor="originHost"
                hint="Needed only if the endpoint above is proxied. Every address on this server — default domains, custom domains, published databases — resolves here. Leave blank if the endpoint already resolves to the machine."
              >
                <Input
                  id="originHost"
                  placeholder="203.0.113.7"
                  autoComplete="off"
                  spellCheck={false}
                  value={originHost}
                  onChange={(e) => setOriginHost(e.target.value)}
                />
              </Field>

              <Field
                label="API key"
                htmlFor="apiKey"
                hint="Settings → Profile → API/CLI. Needs project access. Admin scope also unlocks diagnostics and ingress controls."
              >
                <Input
                  id="apiKey"
                  type="password"
                  required
                  autoComplete="off"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                />
              </Field>
            </div>
          </Card>

          <Card>
            <CardHeader title="Placement" description="Where it is, and what may land on it." />
            <div className="space-y-4 p-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Region"
                  htmlFor="region"
                  hint={
                    "Where the machine is. Tenants land on the nearest one; customers pick its group " +
                    "and get the nearest inside it."
                  }
                >
                  <Select id="region" value={region} onChange={(e) => setRegion(e.target.value)}>
                    <option value="">Not set</option>
                    {(regions ?? []).map((entry) => (
                      <optgroup key={entry.group} label={entry.group}>
                        {entry.regions.map((option) => (
                          <option key={option.code} value={option.code}>
                            {option.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </Select>
                </Field>

                <Field label="Max organizations" htmlFor="maxOrgs" hint="Blank for no limit.">
                  <Input
                    id="maxOrgs"
                    type="number"
                    min={1}
                    value={maxOrgs}
                    onChange={(e) => setMaxOrgs(e.target.value)}
                  />
                </Field>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Latitude"
                  htmlFor="latitude"
                  hint="Optional. Overrides the region's point."
                >
                  <Input
                    id="latitude"
                    type="number"
                    step="any"
                    min={-90}
                    max={90}
                    placeholder="50.11"
                    value={latitude}
                    onChange={(e) => {
                      setLatitude(e.target.value);
                      setCoordinateError(null);
                    }}
                  />
                </Field>

                <Field label="Longitude" htmlFor="longitude" hint="Decimal degrees.">
                  <Input
                    id="longitude"
                    type="number"
                    step="any"
                    min={-180}
                    max={180}
                    placeholder="8.68"
                    value={longitude}
                    onChange={(e) => {
                      setLongitude(e.target.value);
                      setCoordinateError(null);
                    }}
                  />
                </Field>
              </div>

              {coordinateError && <Alert tone="error">{coordinateError}</Alert>}

              <Field
                label="State"
                htmlFor="state"
                hint="Only active servers take new tenants. The rest keep what they have."
              >
                <Select
                  id="state"
                  value={state}
                  onChange={(e) => setState(e.target.value as ServerState)}
                >
                  <option value="active">Active</option>
                  <option value="draining">Draining</option>
                  <option value="disabled">Disabled</option>
                </Select>
              </Field>

              <Field label="Notes" htmlFor="notes">
                <Textarea
                  id="notes"
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Field>
            </div>
          </Card>
        </div>

        <aside className="lg:sticky lg:top-6 space-y-4">
          <Card>
            <CardHeader title="Before you register" />
            <ul className="space-y-3 px-5 py-4 text-xs leading-5 text-fg-subtle">
              <li>
                <span className="text-fg">The key is checked first.</span> An authentication failure
                is refused outright, because it is nearly always a typo that would break every
                tenant later placed here.
              </li>
              <li>
                <span className="text-fg">An unreachable host is accepted.</span> Registering a
                machine that is still booting is reasonable, so only a rejected key stops this.
              </li>
              <li>
                <span className="text-fg">The key never comes back.</span> It is encrypted on
                arrival and only its last four characters are ever returned. Rotating replaces it;
                there is no read path.
              </li>
              {!region && !latitude && (
                <li className="text-warn">
                  With no region and no coordinates, distance skips this server. It takes tenants
                  only when no located server can.
                </li>
              )}
              {/*
                The sibling of the warning above, and the one the region change
                created. A region names an area now rather than a city, so seven of
                the nine reach further than the 500km band placement ranks inside —
                which means two servers legitimately in one region can be too far
                apart for the region alone to separate, and the nearer one loses on
                a tie it should have won. Coordinates are the only thing that
                recovers it, so the prompt appears exactly when it applies.
              */}
              {approximateRegion && !latitude && (
                <li className="text-warn">
                  <span className="text-fg">{approximateRegion.label} covers a wide area.</span>{" "}
                  Placement treats this server as sitting at the region&rsquo;s centre, which may be
                  far from the rack. If you have more than one server here, set the coordinates
                  below so the closer one wins.
                </li>
              )}
            </ul>
          </Card>
        </aside>

        <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-end gap-2 border-t border-line bg-canvas/95 py-3 backdrop-blur lg:col-span-2">
          <Link to="/servers" className={buttonStyles("secondary", "md")}>
            Cancel
          </Link>
          <Button type="submit" loading={create.isPending}>
            Register
          </Button>
        </div>
      </form>

      <Modal
        open={blocker.state === "blocked"}
        onClose={() => blocker.reset?.()}
        title="Leave without registering?"
        description="The API key you pasted will be lost. Nothing has been sent to the engine."
      >
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => blocker.reset?.()}>
            Keep editing
          </Button>
          <Button variant="danger" onClick={() => blocker.proceed?.()}>
            Discard
          </Button>
        </div>
      </Modal>
    </div>
  );
}
